// src/components/common/Pagination.jsx
import React from 'react';

const Pagination = ({ 
  currentPage, 
  totalPages, 
  onPageChange,
  siblingCount = 2,
  showFirstLast = true,
  showPrevNext = true,
  size = 'md',
  variant = 'default'
}) => {
  
  // Size mappings
  const sizes = {
    sm: {
      button: {
        padding: '0.25rem 0.5rem',
        fontSize: '0.75rem',
        minWidth: '2rem',
        height: '2rem',
      },
      text: {
        fontSize: '0.75rem',
      },
      icon: {
        width: '1rem',
        height: '1rem',
      },
    },
    md: {
      button: {
        padding: '0.5rem 0.75rem',
        fontSize: '0.875rem',
        minWidth: '2.5rem',
        height: '2.5rem',
      },
      text: {
        fontSize: '0.875rem',
      },
      icon: {
        width: '1.25rem',
        height: '1.25rem',
      },
    },
    lg: {
      button: {
        padding: '0.75rem 1rem',
        fontSize: '1rem',
        minWidth: '3rem',
        height: '3rem',
      },
      text: {
        fontSize: '1rem',
      },
      icon: {
        width: '1.5rem',
        height: '1.5rem',
      },
    },
  };

  // Variant styles
  const variants = {
    default: {
      button: {
        backgroundColor: '#ffffff',
        color: '#374151',
        border: '1px solid #d1d5db',
      },
      buttonHover: {
        backgroundColor: '#f9fafb',
      },
      buttonActive: {
        backgroundColor: '#3b82f6',
        color: '#ffffff',
        borderColor: '#3b82f6',
      },
      buttonDisabled: {
        opacity: 0.5,
        cursor: 'not-allowed',
        backgroundColor: '#f3f4f6',
      },
    },
    outline: {
      button: {
        backgroundColor: 'transparent',
        color: '#3b82f6',
        border: '1px solid #3b82f6',
      },
      buttonHover: {
        backgroundColor: '#eff6ff',
      },
      buttonActive: {
        backgroundColor: '#3b82f6',
        color: '#ffffff',
        borderColor: '#3b82f6',
      },
      buttonDisabled: {
        opacity: 0.5,
        cursor: 'not-allowed',
        borderColor: '#9ca3af',
        color: '#9ca3af',
      },
    },
    minimal: {
      button: {
        backgroundColor: 'transparent',
        color: '#6b7280',
        border: 'none',
      },
      buttonHover: {
        backgroundColor: '#f3f4f6',
      },
      buttonActive: {
        backgroundColor: '#3b82f6',
        color: '#ffffff',
      },
      buttonDisabled: {
        opacity: 0.3,
        cursor: 'not-allowed',
      },
    },
  };

  const currentSize = sizes[size] || sizes.md;
  const currentVariant = variants[variant] || variants.default;

  const styles = {
    container: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '0.75rem 1rem',
      backgroundColor: '#ffffff',
      borderTop: '1px solid #e5e7eb',
      flexWrap: 'wrap',
      gap: '0.5rem',
    },
    mobileContainer: {
      display: 'flex',
      justifyContent: 'space-between',
      flex: 1,
      '@media (min-width: 640px)': {
        display: 'none',
      },
    },
    desktopContainer: {
      display: 'none',
      '@media (min-width: 640px)': {
        display: 'flex',
        flex: 1,
        alignItems: 'center',
        justifyContent: 'space-between',
      },
    },
    infoSection: {
      display: 'flex',
      alignItems: 'center',
      color: '#4b5563',
    },
    infoText: {
      fontSize: currentSize.text.fontSize,
    },
    infoHighlight: {
      fontWeight: 600,
      color: '#111827',
      margin: '0 0.25rem',
    },
    navSection: {
      display: 'flex',
      gap: '0.25rem',
    },
    buttonGroup: {
      display: 'flex',
      gap: '0.25rem',
      flexWrap: 'wrap',
      justifyContent: 'center',
    },
    button: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      ...currentSize.button,
      ...currentVariant.button,
      borderRadius: '0.375rem',
      cursor: 'pointer',
      transition: 'all 0.2s',
      outline: 'none',
      fontWeight: 500,
      lineHeight: 1,
      whiteSpace: 'nowrap',
    },
    buttonHover: {
      ...currentVariant.buttonHover,
    },
    buttonActive: {
      ...currentVariant.buttonActive,
      zIndex: 10,
    },
    buttonDisabled: {
      ...currentVariant.buttonDisabled,
      pointerEvents: 'none',
    },
    buttonWithIcon: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.25rem',
    },
    icon: {
      width: currentSize.icon.width,
      height: currentSize.icon.height,
      fill: 'none',
      stroke: 'currentColor',
      strokeWidth: 2,
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
    },
    dots: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      ...currentSize.button,
      color: '#6b7280',
      backgroundColor: 'transparent',
      border: 'none',
    },
    firstLastButton: {
      ...currentSize.button,
      ...currentVariant.button,
      borderRadius: '0.375rem',
    },
    prevNextButton: {
      ...currentSize.button,
      ...currentVariant.button,
      borderRadius: '0.375rem',
    },
  };

  const getPageNumbers = () => {
    const totalNumbers = siblingCount * 2 + 3; // siblings + current + first + last
    const totalBlocks = totalNumbers + 2; // including dots

    if (totalPages <= totalBlocks) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    const leftSiblingIndex = Math.max(currentPage - siblingCount, 1);
    const rightSiblingIndex = Math.min(currentPage + siblingCount, totalPages);

    const shouldShowLeftDots = leftSiblingIndex > 2;
    const shouldShowRightDots = rightSiblingIndex < totalPages - 1;

    if (!shouldShowLeftDots && shouldShowRightDots) {
      const leftItemCount = 3 + 2 * siblingCount;
      const leftRange = Array.from({ length: leftItemCount }, (_, i) => i + 1);
      return [...leftRange, '...', totalPages];
    }

    if (shouldShowLeftDots && !shouldShowRightDots) {
      const rightItemCount = 3 + 2 * siblingCount;
      const rightRange = Array.from(
        { length: rightItemCount },
        (_, i) => totalPages - rightItemCount + i + 1
      );
      return [1, '...', ...rightRange];
    }

    if (shouldShowLeftDots && shouldShowRightDots) {
      const middleRange = Array.from(
        { length: rightSiblingIndex - leftSiblingIndex + 1 },
        (_, i) => leftSiblingIndex + i
      );
      return [1, '...', ...middleRange, '...', totalPages];
    }

    return [];
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages && page !== currentPage) {
      onPageChange(page);
    }
  };

  const handlePrev = () => {
    if (currentPage > 1) {
      onPageChange(currentPage - 1);
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      onPageChange(currentPage + 1);
    }
  };

  const handleFirst = () => {
    if (currentPage !== 1) {
      onPageChange(1);
    }
  };

  const handleLast = () => {
    if (currentPage !== totalPages) {
      onPageChange(totalPages);
    }
  };

  const getButtonStyle = (page) => {
    const isActive = page === currentPage;
    const isDisabled = page === '...';

    let baseStyle = { ...styles.button };
    
    if (isActive) {
      baseStyle = { ...baseStyle, ...styles.buttonActive };
    }
    
    if (isDisabled) {
      baseStyle = { ...baseStyle, ...styles.buttonDisabled };
    }

    return baseStyle;
  };

  const pageNumbers = getPageNumbers();

  if (totalPages <= 1) return null;

  return (
    <div style={styles.container}>
      {/* Mobile view */}
      <div style={styles.mobileContainer}>
        <button
          onClick={handlePrev}
          disabled={currentPage === 1}
          style={{
            ...styles.button,
            ...(currentPage === 1 ? styles.buttonDisabled : {}),
          }}
          onMouseEnter={(e) => {
            if (currentPage !== 1) {
              e.currentTarget.style.backgroundColor = currentVariant.buttonHover.backgroundColor;
            }
          }}
          onMouseLeave={(e) => {
            if (currentPage !== 1) {
              e.currentTarget.style.backgroundColor = currentVariant.button.backgroundColor;
            }
          }}
        >
          Previous
        </button>
        <button
          onClick={handleNext}
          disabled={currentPage === totalPages}
          style={{
            ...styles.button,
            ...(currentPage === totalPages ? styles.buttonDisabled : {}),
          }}
          onMouseEnter={(e) => {
            if (currentPage !== totalPages) {
              e.currentTarget.style.backgroundColor = currentVariant.buttonHover.backgroundColor;
            }
          }}
          onMouseLeave={(e) => {
            if (currentPage !== totalPages) {
              e.currentTarget.style.backgroundColor = currentVariant.button.backgroundColor;
            }
          }}
        >
          Next
        </button>
      </div>

      {/* Desktop view */}
      <div style={styles.desktopContainer}>
        <div style={styles.infoSection}>
          <span style={styles.infoText}>
            Page <span style={styles.infoHighlight}>{currentPage}</span> of{' '}
            <span style={styles.infoHighlight}>{totalPages}</span>
          </span>
        </div>

        <div style={styles.buttonGroup}>
          {/* First page button */}
          {showFirstLast && (
            <button
              onClick={handleFirst}
              disabled={currentPage === 1}
              style={{
                ...styles.firstLastButton,
                ...(currentPage === 1 ? styles.buttonDisabled : {}),
              }}
              onMouseEnter={(e) => {
                if (currentPage !== 1) {
                  e.currentTarget.style.backgroundColor = currentVariant.buttonHover.backgroundColor;
                }
              }}
              onMouseLeave={(e) => {
                if (currentPage !== 1) {
                  e.currentTarget.style.backgroundColor = currentVariant.button.backgroundColor;
                }
              }}
              title="First page"
            >
              <svg style={styles.icon} viewBox="0 0 24 24">
                <path d="M18 17l-6-5 6-5v10z" />
                <path d="M12 17l-6-5 6-5v10z" />
              </svg>
            </button>
          )}

          {/* Previous page button */}
          {showPrevNext && (
            <button
              onClick={handlePrev}
              disabled={currentPage === 1}
              style={{
                ...styles.prevNextButton,
                ...(currentPage === 1 ? styles.buttonDisabled : {}),
              }}
              onMouseEnter={(e) => {
                if (currentPage !== 1) {
                  e.currentTarget.style.backgroundColor = currentVariant.buttonHover.backgroundColor;
                }
              }}
              onMouseLeave={(e) => {
                if (currentPage !== 1) {
                  e.currentTarget.style.backgroundColor = currentVariant.button.backgroundColor;
                }
              }}
              title="Previous page"
            >
              <svg style={styles.icon} viewBox="0 0 24 24">
                <path d="M15 18l-6-6 6-6v12z" />
              </svg>
            </button>
          )}

          {/* Page numbers */}
          {pageNumbers.map((page, index) => (
            <button
              key={`${page}-${index}`}
              onClick={() => handlePageChange(page)}
              disabled={page === '...'}
              style={getButtonStyle(page)}
              onMouseEnter={(e) => {
                if (page !== '...' && page !== currentPage) {
                  e.currentTarget.style.backgroundColor = currentVariant.buttonHover.backgroundColor;
                }
              }}
              onMouseLeave={(e) => {
                if (page !== '...' && page !== currentPage) {
                  e.currentTarget.style.backgroundColor = currentVariant.button.backgroundColor;
                }
              }}
            >
              {page === '...' ? '...' : page}
            </button>
          ))}

          {/* Next page button */}
          {showPrevNext && (
            <button
              onClick={handleNext}
              disabled={currentPage === totalPages}
              style={{
                ...styles.prevNextButton,
                ...(currentPage === totalPages ? styles.buttonDisabled : {}),
              }}
              onMouseEnter={(e) => {
                if (currentPage !== totalPages) {
                  e.currentTarget.style.backgroundColor = currentVariant.buttonHover.backgroundColor;
                }
              }}
              onMouseLeave={(e) => {
                if (currentPage !== totalPages) {
                  e.currentTarget.style.backgroundColor = currentVariant.button.backgroundColor;
                }
              }}
              title="Next page"
            >
              <svg style={styles.icon} viewBox="0 0 24 24">
                <path d="M9 18l6-6-6-6v12z" />
              </svg>
            </button>
          )}

          {/* Last page button */}
          {showFirstLast && (
            <button
              onClick={handleLast}
              disabled={currentPage === totalPages}
              style={{
                ...styles.firstLastButton,
                ...(currentPage === totalPages ? styles.buttonDisabled : {}),
              }}
              onMouseEnter={(e) => {
                if (currentPage !== totalPages) {
                  e.currentTarget.style.backgroundColor = currentVariant.buttonHover.backgroundColor;
                }
              }}
              onMouseLeave={(e) => {
                if (currentPage !== totalPages) {
                  e.currentTarget.style.backgroundColor = currentVariant.button.backgroundColor;
                }
              }}
              title="Last page"
            >
              <svg style={styles.icon} viewBox="0 0 24 24">
                <path d="M6 17l6-5-6-5v10z" />
                <path d="M12 17l6-5-6-5v10z" />
              </svg>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default Pagination;